import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

// Default English content (also used as fallback while fetching)
const DEFAULT_CONTENT = {
  nav: {
    home: 'Home', systems: 'ZHELTA Systems', services: 'ZHELTA Services',
    academy: 'ZHELTA Academy', support: 'Support', about: 'About Us',
  },
  header: { search_placeholder: 'Search...', select_language: 'Select Language' },
  featured: { cta: 'Learn More' },
  about: {
    title: 'About ZHELTA',
    body: "Since 2012, ZHELTA has helped people and small businesses use technology with confidence. Today we deliver on that mission across three connected divisions — Systems for managed IT, Services for verified hardware, and Academy for practical, future-ready learning.",
    mission_title: 'Our Mission',
    mission_body: 'Close the technology gap — so every individual, household, and small business can use technology to its full potential, at home, at work, and in the classroom.',
    vision_title: 'Our Vision',
    vision_body: "A world where no home user, small business, or learner is held back by technology — because ZHELTA is there to spot the problem and deliver the fix.",
  },
  services: { title: 'Our Services' },
  why: {
    title: 'Why Choose ZHELTA',
    subtitle: 'Three divisions. One standard of care.',
    one: {
      title: 'One platform. Three powers.',
      body: 'Managed IT, verified hardware, and practical education — engineered to work as one so nothing falls through the cracks.',
    },
    two: {
      title: 'Built with intention.',
      body: 'Every system, every lesson, every product is picked for real-world clarity — and designed to scale as you grow.',
    },
    three: {
      title: 'Focused on what\u2019s next.',
      body: "We don\u2019t just solve today\u2019s problems — we prepare you for whatever tomorrow\u2019s technology will ask of you.",
    },
  },
  shop: { label: 'ZHELTA SERVICES', title_a: '', title_b: '', body: '', cta: 'Shop Our Tech Store' },
  contact: {
    eyebrow: 'GET IN TOUCH', title: "Let's build what's next, together.", body: '',
    name: 'Full name', email: 'Email address', subject: 'Subject', message: 'Your message',
    submit: 'Send message', success: 'Thanks!', error: 'Something went wrong.',
  },
  footer: {
    tagline: '', company: 'Company', platform: 'Platform', support: 'Support', legal: 'Legal',
    rights: 'All rights reserved.', slogan: "Built for What's Next.",
  },
  systems_nav: {
    home: 'Home', solutions: 'Solutions', products: 'Products', pricing: 'Pricing',
    resources: 'Resources', book_demo: 'Book a Demo', get_started: 'Get Started',
  },
  academy_nav: {
    home: 'Home', programs: 'Programs', tracks: 'Tracks', about: 'About', contact: 'Contact',
    talk_to_us: 'Talk to Us', enroll_now: 'Enroll Now', search_placeholder: 'Search programs...',
  },
  systems: {
    hero_eyebrow: 'Built on more than a decade of practice',
    hero_title_a: 'Enterprise IT Solutions.',
    hero_title_b: 'Simplified.',
    hero_body: 'Professional IT management and cybersecurity protection that keeps your business running smoothly, securely, and efficiently.',
    hero_primary_cta: 'Schedule Consultation',
    hero_secondary_cta: 'View Solutions',
    why_eyebrow: 'Why Zhelta Systems',
    why_title: 'Built for modern businesses',
    why_subtitle: 'Technology infrastructure that delivers security, reliability, and peace of mind.',
    solutions_title: 'Comprehensive IT solutions',
    solutions_subtitle: 'Everything you need to manage, secure, and scale your technology infrastructure.',
    solutions_view_all: 'View All Solutions',
    process_eyebrow: 'SIMPLE PROCESS',
    process_title: 'Getting started is simple',
    process_subtitle: 'From consultation to ongoing support, we make IT management effortless.',
    expertise_title: 'Enterprise-grade technology expertise',
    expertise_subtitle: "We deliver expert services and support for the world's leading technology platforms.",
    compare_title: 'ZHELTA vs. Other MSPs',
    compare_subtitle: 'See how we deliver superior value and service',
    cta_eyebrow: 'Transform Your IT Infrastructure',
    cta_title: 'Ready to modernize your technology infrastructure?',
    cta_body: 'Speak with our specialists and discover how ZHELTA Systems can support your devices, infrastructure, and business systems.',
    cta_primary: 'Schedule Consultation',
    cta_secondary: 'Contact Sales',
    faq_eyebrow: 'FAQ',
    faq_title: 'Answers, before you ask',
    faq_subtitle: 'The most common questions from IT leaders evaluating ZHELTA Systems.',
  },
  academy: {
    hero_eyebrow: 'ZHELTA ACADEMY',
    hero_title_a: 'Empower Your Career.',
    hero_title_b: 'Master In-Demand Skills.',
    hero_title_c: 'Learn with ZHELTA.',
    hero_body: "Accessible, expert-led education designed to equip learners with practical skills for today's digital world.",
    hero_primary_cta: 'Explore All Programs',
    hero_note: 'ZHELTA Academy provides practical, future-ready education aligned with real-world needs.',
    why_eyebrow: 'Why Choose Us',
    why_title: 'Future-Ready Learning',
    why_quote: "Transforming today's potential into tomorrow's innovation through a hyper-flexible, mentor-driven IT curriculum.",
    programs_eyebrow: 'World-Class Curriculum',
    programs_title: 'Explore Our Core Programs',
    programs_view_all: 'View All Programs',
    tracks_eyebrow: 'Growth Path',
    tracks_title: 'Start at Your Level, Grow Your Expertise',
    tracks_subtitle: 'Our learning tracks are tailored for beginner, intermediate, and advanced learners, ensuring a perfect starting point.',
    journey_eyebrow: 'Your Journey',
    journey_title: 'Four Simple Steps to Success',
    testimonials_eyebrow: 'Success Stories',
    testimonials_title: 'Hear From Our Learners',
    testimonials_subtitle: 'Real stories from students who transformed their careers through our programs.',
    cta_eyebrow: 'Start Your Journey',
    cta_title: 'Ready to Transform Your Future?',
    cta_body: 'Join ZHELTA Academy and master in-demand skills with our expert-led programs and mentorship.',
    cta_primary: 'Browse Programs',
    cta_secondary: 'View Tracks',
    faq_eyebrow: 'FAQ',
    faq_title: 'Questions learners ask us',
    faq_subtitle: 'Everything you need to know before enrolling in ZHELTA Academy.',
  },
  seo: {
    home: {
      title: "ZHELTA \u2014 Technology, Learning, and Digital Services for what's next",
      description: "ZHELTA brings enterprise IT, future-ready education, and digital services together in one platform. Built for what's next.",
    },
    systems: {
      title: 'ZHELTA Systems \u2014 Enterprise IT Solutions, Simplified',
      description: "Professional IT management and cybersecurity protection that keeps your business running smoothly, securely, and efficiently.",
    },
    academy: {
      title: 'ZHELTA Academy \u2014 Practical, Future-Ready IT Education',
      description: "Accessible, expert-led education designed to equip learners with practical skills for today's digital world. Master in-demand IT skills with ZHELTA Academy.",
    },
  },
};

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: DEFAULT_CONTENT,
  loading: false,
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const queryLang = params.get('lang');
      if (queryLang) return queryLang;
    } catch (_e) { /* noop */ }
    return localStorage.getItem('zhelta_lang') || 'en';
  });
  const [t, setT] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(false);

  const fetchContent = useCallback(async (code) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/i18n/${encodeURIComponent(code)}`);
      if (res.data && res.data.content) {
        // Merge with defaults to avoid missing keys
        setT({ ...DEFAULT_CONTENT, ...res.data.content });
      }
    } catch (e) {
      console.error('i18n fetch failed', e);
      setT(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent(lang);
  }, [lang, fetchContent]);

  // Apply RTL direction to <html> for Arabic and Hebrew
  useEffect(() => {
    const rtlLangs = new Set(['ar', 'he', 'fa', 'ur']);
    const isRTL = rtlLangs.has(lang);
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = (code) => {
    localStorage.setItem('zhelta_lang', code);
    setLangState(code);
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, loading }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
